// DocumentList.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaEllipsisV, FaFilePdf } from 'react-icons/fa';
import { Menu, Item as MenuItem, useContextMenu } from 'react-contexify';
import 'react-contexify/dist/ReactContexify.css';
import * as XLSX from 'xlsx';
import './DocumentList.css';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';

function DocumentList() {
  const { currentUser } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');

  // Unique menu id for the context menu
  const MENU_ID = 'actions-menu';

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file && currentUser) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name);
  
        const token = await currentUser.getIdToken();
  
        const response = await fetch(`${API_BASE_URL}/upload_document`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });
  
        if (!response.ok) {
          throw new Error(`Error uploading document: ${response.statusText}`);
        }
  
        const newDocument = await response.json();
        setDocuments((prevDocs) => [...prevDocs, newDocument]);
      } catch (error) {
        console.error('Error uploading document:', error);
      }
    }
  };
  
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!currentUser) return;
  
      setIsLoading(true);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/documents`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        if (!response.ok) {
          throw new Error(`Error fetching documents: ${response.statusText}`);
        }
  
        const data = await response.json();
        setDocuments(data.documents);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchDocuments();
  }, [currentUser]);

  const handleDeleteDocument = async (documentId) => {
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();

        const response = await fetch(`${API_BASE_URL}/delete_document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ document_id: documentId }),
        });

        if (!response.ok) {
          throw new Error(`Error deleting document: ${response.statusText}`);
        }

        setDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== documentId));
      } catch (error) {
        console.error('Error deleting document:', error);
      }
    }
  };

  const handleDownloadExcel = async (documentId, documentName) => {
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();

        const response = await fetch(`${API_BASE_URL}/get_data/${documentId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          alert('No data available for Excel download');
          return;
        }

        const extractedDataPerPage = await response.json();

        // Proceed to generate the Excel file using 'extractedDataPerPage'
        const workbook = XLSX.utils.book_new();

        let allRows = [];
        let headerRowSet = false;
        let headerRow = [];

        const pages = Object.keys(extractedDataPerPage).sort((a, b) => Number(a) - Number(b));

        pages.forEach((pageNum) => {
          const pageData = extractedDataPerPage[pageNum];

          const lineItems = pageData.line_items || [];

          // Get unique fields, line items, and total bill fields
          const uniqueFields = pageData.unique_fields || {};
          const totalBillFields = pageData.total_bill || {};

          // Define the order of line item fields
          const lineItemFields = lineItems.length > 0 ? Object.keys(lineItems[0]) : [];
          const orderedLineItemFields = [
            'Invoice Line Item Number',
            'Invoice Line Description',
            ...lineItemFields.filter(
              (field) => field !== 'Invoice Line Item Number' && field !== 'Invoice Line Description'
            ),
          ];

          // Ensure Invoice Number is the first column
          const uniqueFieldsOrdered = { 'Invoice Number': uniqueFields['Invoice Number'], ...uniqueFields };
          delete uniqueFieldsOrdered['Invoice Number'];

          // Prepare header row only once
          if (!headerRowSet) {
            headerRow = [
              'Page Number',
              'Invoice Number',
              ...Object.keys(uniqueFieldsOrdered),
              ...orderedLineItemFields,
              ...Object.keys(totalBillFields),
            ];
            allRows.push(headerRow);
            headerRowSet = true;
          }

          // Create rows for each line item, repeating unique fields and total bill fields for each row
          lineItems.forEach((item) => {
            const row = [
              pageNum, // Add page number
              uniqueFields['Invoice Number'] || '',
              ...Object.values(uniqueFieldsOrdered),
              ...orderedLineItemFields.map((field) => item[field] || ''),
              ...Object.values(totalBillFields),
            ];
            allRows.push(row);
          });
        });

        if (allRows.length <= 1) {
          alert('No line items found in the document.');
          return;
        }

        const worksheet = XLSX.utils.aoa_to_sheet(allRows);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice Data');
        XLSX.writeFile(workbook, `${documentName.replace('.pdf', '')}.xlsx`);
      } catch (error) {
        console.error('Error downloading Excel:', error);
      }
    }
  };

  // useContextMenu hook with correct id
  const { show } = useContextMenu({
    id: MENU_ID,
  });

  const handleActionMenuClick = (documentId, documentName, action) => {
    if (action === 'delete') {
      handleDeleteDocument(documentId);
    } else if (action === 'download_excel') {
      handleDownloadExcel(documentId, documentName);
    }
  };

  const categories = ['All', 'Delivery Notes', 'Invoices', 'Purchase Orders', 'Credit Notes', 'Debit Notes'];
  const statuses = ['All', 'To Review', 'Confirmed', 'Rejected'];

  const filteredDocuments = documents.filter((doc) => {
    const categoryMatch = selectedCategory === 'All' || doc.category === selectedCategory;
    const statusMatch = selectedStatus === 'All' || doc.status === selectedStatus;
    return categoryMatch && statusMatch;
  });

  if (!currentUser) {
    return <div>Please log in to view your documents.</div>;
  }

  if (isLoading) return <div className="loading">Loading...</div>;

  return (
    <div className="document-page-full">
      <div className="document-container">
        {/* Filter Section */}
        <div className="document-filters">
          <div className="filter-container">
            <label>Category:</label>
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-container">
            <label>Status:</label>
            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Document Upload Section */}
        <div className="upload-container">
          <input
            type="file"
            id="file-upload"
            onChange={handleFileUpload}
            accept=".pdf"
            style={{ display: 'none' }}
          />
          <label htmlFor="file-upload" className="upload-button">
            Upload New Document
          </label>
        </div>

        {/* Document Table */}
        <div className="document-list-table">
          {filteredDocuments.length === 0 ? (
            <p className="no-documents-message">No documents found. Upload a document to get started.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Document Name</th>
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="document-row">
                    <td>
                      <span className={`status-badge ${doc.status.toLowerCase().replace(/\s+/g, '-')}`}>
                        {doc.status}
                      </span>
                    </td>
                    <td>
                      <Link to={`/validate/${doc.id}`} className="document-link">
                        <FaFilePdf className="document-icon" /> {doc.name}
                      </Link>
                    </td>
                    <td>{doc.category}</td>
                    <td>
                      <button
                        className="actions-button"
                        onClick={(e) => {
                          e.preventDefault();
                          show({
                            event: e,
                            props: { documentId: doc.id, documentName: doc.name },
                          });
                        }}
                      >
                        <FaEllipsisV />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Context Menu for Actions */}
      <Menu id={MENU_ID}>
        <MenuItem onClick={({ props }) => handleActionMenuClick(props.documentId, props.documentName, 'delete')}>
          Delete
        </MenuItem>
        <MenuItem
          onClick={({ props }) => handleActionMenuClick(props.documentId, props.documentName, 'download_excel')}
        >
          Download Excel
        </MenuItem>
      </Menu>
    </div>
  );
}

export default DocumentList;
